import { extractApiError, type HttpApiClient } from "@vana/core";
import { type EducationTopic, type TopicType } from "../../domain/entities/education-topic";
import {
  CompleteTrainingError,
  ListTopicsError,
  MarkTopicError,
  UnmarkTopicError,
} from "../../domain/errors/errors";
import {
  type CompleteTrainingParams,
  type EducationRepository,
  type MarkTopicParams,
} from "../../domain/repos/education-repo";
import {
  type TopicResponse,
  type TopicsResponse,
} from "../../infrastructure/responses/topics-response";

export class EducationRepo implements EducationRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listTopics(requestId: string): Promise<EducationTopic[]> {
    try {
      const response = await this.apiClient.get<TopicsResponse>(
        `v1/requests/${requestId}/education/topics`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListTopicsError(message, apiError);
    }
  }

  async markTopic({ requestId, topic, lat, long }: MarkTopicParams): Promise<EducationTopic> {
    try {
      const response = await this.apiClient.post<
        { topic: TopicType; lat: number; long: number },
        TopicResponse
      >(`v1/requests/${requestId}/education/topics`, { topic, lat, long });
      if (!response.data) throw new MarkTopicError("Empty mark response");
      return response.data;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new MarkTopicError(message, apiError);
    }
  }

  async unmarkTopic(requestId: string, topic: TopicType): Promise<void> {
    try {
      await this.apiClient.delete(`v1/requests/${requestId}/education/topics/${topic}`);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UnmarkTopicError(message, apiError);
    }
  }

  async completeTraining({
    requestId,
    lat,
    long,
  }: CompleteTrainingParams): Promise<void> {
    try {
      await this.apiClient.post<{ lat: number; long: number }, unknown>(
        `v1/requests/${requestId}/training`,
        { lat, long },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new CompleteTrainingError(message, apiError);
    }
  }
}
